import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import {
  Sparkles,
  CalendarDays,
  Clock,
  Loader2,
  CheckCircle2,
  ArrowRight,
  Wand2,
  Music,
  Palette,
  Cpu,
  Users,
  Heart,
  Globe2,
} from "lucide-react";
import { Container, FadeIn, SectionDivider } from "@/components/Section";
import PageHero from "@/components/PageHero";
import { IMG } from "@/lib/data";
import ReferralSourceSelector from "@/components/ReferralSourceSelector";
import NextStepsBlock from "@/components/NextStepsBlock";
import AcuityScheduleBlock from "@/components/AcuityScheduleBlock";
import PartnersStrip from "@/components/PartnersStrip";
import SEOHead from "@/components/SEOHead";
import StayConnected from "@/components/StayConnected";
import { pageSource, deviceSource } from "@/lib/tracking";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTIVITIES = [
  { icon: Wand2, title: "Create Your Own Magic", desc: "Imagination labs where children invent characters, worlds and rituals." },
  { icon: Palette, title: "Immersive Mask Making", desc: "Craft + storytelling — rooted in African and Caribbean traditions." },
  { icon: Music, title: "AI Beat Making", desc: "Hands-on with creative AI to build their first tracks." },
  { icon: Sparkles, title: "Interactive Playground", desc: "Movement-led projection-mapped games for embodied learning." },
  { icon: Cpu, title: "Projection Mapping & XR Storytelling", desc: "Painting with light, projection theatre, headset journeys." },
  { icon: Heart, title: "Music & Movement", desc: "Confidence, rhythm and self-expression through diaspora dance." },
  { icon: Globe2, title: "Collaborative World-Building", desc: "Group creation of mini cultural universes — story, set, sound." },
  { icon: Users, title: "Performance & Imagination Labs", desc: "Safe stages for shy and bold voices alike." },
];

const PILLARS = [
  { title: "Flexible creative learning", desc: "A curriculum that bends to the child — not the other way round. We focus on what lights them up." },
  { title: "Confidence building", desc: "Through ritual, performance and shared making — young people leave louder, calmer and more themselves." },
  { title: "Creativity × technology", desc: "Children learn AI, XR and immersive tools as expressive instruments, not gadgets." },
  { title: "Social development", desc: "Mixed-age sessions teach collaboration, conflict-resolution and chosen-family bonds." },
  { title: "Cultural storytelling", desc: "Global majority histories, languages and folklore woven into every project." },
  { title: "Imagination through immersive arts", desc: "We use projection, sound and live performance to let imagination feel real." },
];

const DAYS = [
  { value: "wednesday", label: "Wednesday" },
  { value: "thursday", label: "Thursday" },
  { value: "either", label: "Either" },
];

export default function HomeEducation() {
  return (
    <div className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Home Education Programme · Wed & Thu Mornings · TOK Medway"
        description="A weekly creative home education programme for global majority families and alternative learners. Wednesdays and Thursdays in Medway — XR storytelling, AI beat making, immersive mask making and interactive playground. Funded places available."
        image={IMG.playground}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "EducationalOccupationalProgram",
          name: "TOK Home Education Programme",
          description: "Weekly creative home education for global majority families — Wednesdays and Thursdays in Medway.",
          provider: { "@type": "Organization", name: "Temple of Kulture" },
          educationalProgramMode: "Part-time",
          occupationalCategory: "Creative learning",
        }}
      />
      <PageHero
        eyebrow="Home Education · Under 16"
        title="Creative learning, immersive worlds, your child's pace"
        subtitle="A weekly home education programme for global majority families and alternative learners. Wednesdays and Thursdays in Medway — culturally rooted, future-facing, never traditional school."
        image={IMG.playground}
      />

      {/* When & Where */}
      <section className="py-16 sm:py-20 border-y border-amber-500/10 bg-black">
        <Container className="grid lg:grid-cols-3 gap-8">
          <InfoCard
            icon={CalendarDays}
            label="When"
            title="Wed & Thu mornings"
            body="Term-time weekly. Morning into lunchtime sessions across two consistent days, so families can plan their week around it."
          />
          <InfoCard
            icon={Clock}
            label="Sessions"
            title="Morning · Lunch"
            body="Drop-off at the start of session. Pick-up after a shared creative-lunch ritual. Older siblings welcome."
          />
          <InfoCard
            icon={Heart}
            label="Investment"
            title="Paid programme"
            body="Termly fee covers all materials, immersive tech, snacks and lunch ritual. Funded places open periodically — register interest below."
          />
        </Container>
      </section>

      {/* Who it's for */}
      <SectionDivider label="Who it's for" />
      <Container className="pb-8">
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <FadeIn>
            <p className="tok-eyebrow">For families seeking…</p>
            <h2 className="tok-headline-xl mt-3">
              Not school. <br />
              <span className="text-amber-400">A culture.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={0.1}>
            <ul className="space-y-3 text-zinc-300 leading-[1.75]">
              {[
                "Home-educated children and families",
                "Global majority families",
                "Alternative education learners",
                "Creative young people who don't thrive in traditional classrooms",
                "Neurodivergent learners welcome",
              ].map((x) => (
                <li
                  key={x}
                  className="flex gap-3"
                  data-testid={`home-ed-audience-${x.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                >
                  <span className="mt-2 inline-block h-1.5 w-1.5 bg-amber-400 flex-shrink-0" />
                  {x}
                </li>
              ))}
            </ul>
          </FadeIn>
        </div>
      </Container>

      {/* Activities */}
      <SectionDivider label="What they'll do" />
      <Container className="pb-20">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {ACTIVITIES.map((a, i) => (
            <FadeIn key={a.title} delay={i * 0.04}>
              <div
                data-testid={`home-ed-activity-${i}`}
                className="border border-white/5 bg-black p-6 h-full hover:border-amber-400/40 transition-colors"
              >
                <a.icon className="h-5 w-5 text-amber-400" />
                <h3 className="tok-headline-md mt-4 text-lg">{a.title}</h3>
                <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                  {a.desc}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Container>

      {/* The promise */}
      <SectionDivider label="Our promise" />
      <Container className="pb-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {PILLARS.map((p) => (
            <div
              key={p.title}
              className="border border-white/5 bg-zinc-950 p-6"
              data-testid={`home-ed-pillar-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
            >
              <h3 className="tok-headline-md text-lg">{p.title}</h3>
              <p className="mt-2 text-sm text-zinc-400 leading-relaxed">
                {p.desc}
              </p>
            </div>
          ))}
        </div>
      </Container>

      {/* Enquiry form */}
      <SectionDivider label="Register interest" />
      <Container className="pb-16 max-w-5xl">
        <div className="grid lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <HomeEdForm />
          </div>
          <div className="lg:col-span-2 space-y-5">
            <AcuityScheduleBlock
              eyebrow="Before you enrol"
              title="Book a free family taster"
              copy="A 45-minute taster — meet the practitioners, see the space, ask anything."
              slug="home-education"
              audience="Under 16 · with parent"
              duration="45 mins"
              fallbackTo="#register-interest"
              fallbackLabel="Join the waitlist"
              testId="acuity-home-ed-taster"
            />
            <AcuityScheduleBlock
              eyebrow="Curriculum chat"
              title="Talk to our learning lead"
              copy="20 minutes to walk through approach, accessibility and term schedule."
              slug="home-education"
              audience="Parents / guardians"
              duration="20 mins"
              fallbackTo="/join"
              fallbackLabel="Get updates"
              testId="acuity-home-ed-curriculum"
            />
          </div>
        </div>
      </Container>

      <NextStepsBlock
        title="Continue the journey"
        steps={[
          { label: "Under 12", title: "Interactive Playground", desc: "Sensory-aware projection play for younger siblings.", to: "/interactive-playground", cta: "Sessions →", testid: "home-ed-next-playground" },
          { label: "7 – 16", title: "After School Clubs", desc: "Weekly clubs in projection mapping, AI, music tech.", to: "/after-school-clubs", cta: "Discover →", testid: "home-ed-next-after-school" },
          { label: "16 – 25", title: "LEVEL UP", desc: "When your child outgrows our youth programme.", to: "/level-up-xr", cta: "Apply →", testid: "home-ed-next-level-up" },
          { label: "Join", title: "Join the Temple", desc: "Get the next term's open day first.", to: "/join", cta: "Sign up →", testid: "home-ed-next-join" },
        ]}
      />

      {/* Funders & council partnerships specific to Home Education */}
      <section className="py-20 sm:py-24 bg-zinc-950 border-t border-amber-500/10">
        <PartnersStrip
          eyebrow="In partnership with"
          title="Real councils, real schools, real funders"
          copy="Our Home Education programme works alongside Medway and Kent local authorities, Arts Council England funding and the wider youth and alternative-provision ecosystem."
          programmeKey="home-education"
          variant="card"
          testId="home-ed-partners"
        />
      </section>

      {/* WhatsApp opt-in — weekly programme updates */}
      <StayConnected
        variant="whatsapp"
        pageSource="home-education"
        testid="home-ed-stay"
        whatsappHeadline="WEEKLY UPDATES FOR FAMILIES"
        whatsappTagline="Session reminders, programme launches and Wed/Thu logistics — straight to your WhatsApp."
        whatsappCta="Join the Channel"
        bullets={[
          "Weekly session reminders (Wed & Thu)",
          "Term dates and half-term programming",
          "New cohort applications",
          "Family workshops and trips",
          "Parent community calls",
        ]}
      />
    </div>
  );
}

function InfoCard({ icon: Icon, label, title, body }) {
  return (
    <div className="border border-white/5 p-7" data-testid={`home-ed-info-${label.toLowerCase()}`}>
      <Icon className="h-5 w-5 text-amber-400" />
      <p className="mt-4 tok-eyebrow-sm">{label}</p>
      <h3 className="tok-headline-md mt-1 text-2xl">{title}</h3>
      <p className="mt-3 text-sm text-zinc-400 leading-relaxed">{body}</p>
    </div>
  );
}

function HomeEdForm() {
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    phone: "",
    children_count: 1,
    age_range: "",
    preferred_day: "either",
    start_date: "",
    interests: [],
    funded_place_interest: false,
    message: "",
    referral_source: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const toggleInterest = (interest) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(interest)
        ? f.interests.filter((i) => i !== interest)
        : [...f.interests, interest],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.parent_name || !form.email || !form.age_range) {
      toast.error("Parent name, email and child's age range are required.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/home-education`, {
        ...form,
        children_count: Number(form.children_count) || 1,
        page_source: pageSource(),
        device_source: deviceSource(),
      });
      setDone(true);
      toast.success("Enquiry received — we'll be in touch.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        className="border border-amber-400/40 bg-amber-400/5 p-10 text-center"
        data-testid="home-ed-success"
      >
        <CheckCircle2 className="h-10 w-10 text-amber-400 mx-auto" />
        <h2 className="tok-headline-md mt-4">Enquiry sent.</h2>
        <p className="mt-3 text-zinc-300 max-w-xl mx-auto">
          The TOK Home Education team at{" "}
          <span className="text-amber-400">info@templeofkulture.co.uk</span> will
          reach out within five working days with available start dates,
          pricing and a chance to visit a session.
        </p>
      </motion.div>
    );
  }

  const interestOptions = ACTIVITIES.map((a) => a.title);

  return (
    <form
      onSubmit={submit}
      className="space-y-7"
      data-testid="home-ed-form"
      noValidate
    >
      <div className="grid sm:grid-cols-2 gap-5">
        <Labelled label="Parent / guardian name *">
          <input
            type="text"
            required
            value={form.parent_name}
            onChange={set("parent_name")}
            data-testid="home-ed-parent-name"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Email *">
          <input
            type="email"
            required
            value={form.email}
            onChange={set("email")}
            data-testid="home-ed-email"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Phone (optional)">
          <input
            type="tel"
            value={form.phone}
            onChange={set("phone")}
            data-testid="home-ed-phone"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Number of children">
          <input
            type="number"
            min={1}
            max={10}
            value={form.children_count}
            onChange={set("children_count")}
            data-testid="home-ed-children-count"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Child age range *">
          <input
            type="text"
            required
            value={form.age_range}
            onChange={set("age_range")}
            placeholder="e.g. 7–11"
            data-testid="home-ed-age-range"
            className={INPUT}
          />
        </Labelled>
        <Labelled label="Ideal start date (optional)">
          <input
            type="text"
            value={form.start_date}
            onChange={set("start_date")}
            placeholder="e.g. September"
            data-testid="home-ed-start-date"
            className={INPUT}
          />
        </Labelled>
      </div>

      <Labelled label="Preferred day">
        <div className="flex flex-wrap gap-2">
          {DAYS.map((d) => (
            <button
              type="button"
              key={d.value}
              onClick={() =>
                setForm((f) => ({ ...f, preferred_day: d.value }))
              }
              data-testid={`home-ed-day-${d.value}`}
              className={`tok-cta-label px-4 py-2.5 border transition-colors ${
                form.preferred_day === d.value
                  ? "bg-amber-400 text-black border-amber-400"
                  : "border-white/10 hover:border-amber-400 hover:text-amber-400 text-zinc-300"
              }`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </Labelled>

      <Labelled label="Activities your child would love">
        <div className="flex flex-wrap gap-2" data-testid="home-ed-interests">
          {interestOptions.map((i) => {
            const on = form.interests.includes(i);
            return (
              <button
                type="button"
                key={i}
                onClick={() => toggleInterest(i)}
                data-testid={`home-ed-interest-${i.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold border transition-colors ${
                  on
                    ? "bg-amber-400 text-black border-amber-400"
                    : "border-white/10 hover:border-amber-400 hover:text-amber-400 text-zinc-300"
                }`}
              >
                {i}
              </button>
            );
          })}
        </div>
      </Labelled>

      <Labelled label="Anything else?">
        <textarea
          rows={4}
          value={form.message}
          onChange={set("message")}
          data-testid="home-ed-message"
          placeholder="Learning style, accessibility, what your child needs to feel safe…"
          className={INPUT}
        />
      </Labelled>

      <ReferralSourceSelector
        value={form.referral_source}
        onChange={(v) => setForm((f) => ({ ...f, referral_source: v }))}
        testIdPrefix="home-ed-referral"
        required={false}
      />

      <label className="flex items-start gap-3 text-sm text-zinc-300">
        <input
          type="checkbox"
          checked={form.funded_place_interest}
          onChange={set("funded_place_interest")}
          data-testid="home-ed-funded"
          className="mt-1 accent-amber-400"
        />
        <span>
          We&apos;d like to be considered for a funded place if one becomes
          available.
        </span>
      </label>

      <button
        type="submit"
        disabled={submitting}
        data-testid="home-ed-submit"
        className="tok-cta-label inline-flex items-center gap-3 bg-amber-400 hover:bg-amber-300 disabled:opacity-60 text-black px-8 py-4 transition-colors"
      >
        {submitting ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ArrowRight className="h-4 w-4" />
        )}
        {submitting ? "Sending…" : "Register interest"}
      </button>
    </form>
  );
}

const INPUT =
  "w-full bg-black border border-white/10 px-4 py-3 text-white focus:outline-none focus:border-amber-400 transition-colors";

function Labelled({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.32em] font-bold text-zinc-400">
        {label}
      </span>
      <div className="mt-2">{children}</div>
    </label>
  );
}
